import { useId, useRef, useState, type MouseEvent } from "react";

import type { IntradayCumulativePoint } from "../../lib/intradayPnl";
import { formatTimeShort } from "../../lib/date";
import { formatMoney } from "../../lib/money";

// Intraday running-P&L line, a single-day sibling of the dashboard's
// Cumulative P&L hero (EquityCurveChart). Unlike that chart — which spaces
// session points evenly by index — this one positions each closed trade by its
// actual close time, so a cluster of scalps reads as a cluster and a quiet
// stretch reads as a gap. Hand-rolled SVG in the same `0 0 1000 192` viewBox
// the other analytics widgets use, with a dashed $0 baseline, a right-hand
// value axis (high / $0 / low at their true heights), and a persistent dot on
// the day's close. The curve is split at $0: green at/above, red below.

const VB_W = 1000;
const VB_H = 192;
const PAD_Y = 12;
// Horizontal inset so the first/last vertices and the close dot sit inside the
// plot rather than half-clipped against the card edge.
const PAD_X = 16;

const SUCCESS = "var(--color-success)";
const DANGER = "var(--color-danger)";

interface IntradayPnlChartProps {
  points: ReadonlyArray<IntradayCumulativePoint>;
  currency?: string;
  // Fires with the closed trade under the cursor as the user hovers the plot,
  // and null on leave — lets the panel headline track the running total at the
  // hovered time and fall back to the close when idle.
  onActiveChange?: (point: IntradayCumulativePoint | null) => void;
}

// Compact, always-signed axis label: "+$408", "+$0", "-$169".
function signedCompact(value: number, currency: string): string {
  const abs = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Math.abs(value));
  return `${value < 0 ? "-" : "+"}${abs}`;
}

export function IntradayPnlChart({
  points,
  currency = "USD",
  onActiveChange,
}: IntradayPnlChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<number | null>(null);
  // Unique clip/gradient ids so multiple chart instances on a page never
  // collide (colons from useId aren't valid inside a url(#...) reference).
  const idBase = useId().replace(/:/g, "");
  const aboveClipId = `intraday-above-${idBase}`;
  const belowClipId = `intraday-below-${idBase}`;
  const greenGradId = `intraday-green-${idBase}`;
  const redGradId = `intraday-red-${idBase}`;

  const firstPoint = points[0];
  const lastPoint = points[points.length - 1];
  if (firstPoint === undefined || lastPoint === undefined) {
    return (
      <div className="relative h-full w-full" data-testid="intraday-pnl-chart">
        <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-on-surface-muted">
          No closed trades on this day
        </p>
      </div>
    );
  }

  // Time domain: first close .. last close, with a small left margin so the
  // line rises from a $0 "open" anchor instead of starting mid-air. A flat or
  // single-trade day still gets a positive span (min 1 minute), so the x scale
  // never collapses to a divide-by-zero.
  const firstMs = firstPoint.closedAtMs;
  const lastMs = lastPoint.closedAtMs;
  const span = Math.max(lastMs - firstMs, 60_000);
  const xMin = firstMs - span * 0.04;
  const xMax = lastMs;
  const xRange = xMax - xMin || 1;

  // Series: a $0 open anchor at xMin, then each closed trade's running total.
  const series: Array<{ x: number; y: number; label: string; value: string }> = [
    { x: xMin, y: 0, label: "Open", value: formatMoney("0", { currency }) },
    ...points.map((point) => ({
      x: point.closedAtMs,
      y: point.cumulative,
      label: formatTimeShort(point.closedAt),
      value: formatMoney(point.cumulative.toFixed(2), { currency }),
    })),
  ];

  const yMax = Math.max(0, ...series.map((point) => point.y));
  const yMin = Math.min(0, ...series.map((point) => point.y));
  const yRange = yMax - yMin || 1;

  const xScale = (x: number) => PAD_X + ((x - xMin) / xRange) * (VB_W - 2 * PAD_X);
  const yScale = (y: number) => PAD_Y + (1 - (y - yMin) / yRange) * (VB_H - 2 * PAD_Y);
  const zeroY = yScale(0);
  const highY = yScale(yMax);
  const lowY = yScale(yMin);

  const pts: ReadonlyArray<readonly [number, number]> = series.map(
    (point) => [xScale(point.x), yScale(point.y)] as const,
  );
  const linePath = pts
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point[0].toFixed(2)} ${point[1].toFixed(2)}`)
    .join(" ");
  const startPt = pts[0];
  const endPt = pts[pts.length - 1];
  const areaPath =
    startPt && endPt
      ? `${linePath} L ${endPt[0].toFixed(2)} ${zeroY.toFixed(2)} L ${startPt[0].toFixed(2)} ${zeroY.toFixed(2)} Z`
      : "";

  // Tone of the close dot follows the day's final total.
  const closeColor = lastPoint.cumulative >= 0 ? SUCCESS : DANGER;

  // Gradient bounds clamped so an all-positive or all-negative day never feeds a
  // degenerate (zero-height) gradient.
  const greenGradY2 = Math.max(zeroY, PAD_Y + 1);
  const redGradY1 = Math.min(zeroY, VB_H - PAD_Y - 1);

  const handleMove = (event: MouseEvent<HTMLDivElement>) => {
    if (ref.current === null) return;
    const rect = ref.current.getBoundingClientRect();
    if (rect.width === 0) return;
    const xPct = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const targetX = xPct * VB_W;
    // Snap to the nearest real trade vertex by x. Skip index 0 (the synthetic
    // $0 open anchor) so the tooltip always names an actual close.
    let best = 1;
    let bestDist = Infinity;
    for (let i = 1; i < pts.length; i += 1) {
      const candidate = pts[i];
      if (candidate === undefined) continue;
      const dist = Math.abs(candidate[0] - targetX);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    setActive(best);
    onActiveChange?.(points[best - 1] ?? null);
  };
  const handleLeave = () => {
    setActive(null);
    onActiveChange?.(null);
  };

  const activePt = active !== null ? pts[active] : undefined;
  const activeRow = active !== null ? series[active] : undefined;
  const activeFrac = activePt !== undefined ? activePt[0] / VB_W : null;
  const activeLeft = activeFrac !== null ? `${(activeFrac * 100).toFixed(2)}%` : undefined;
  const activeTop = activePt !== undefined ? `${((activePt[1] / VB_H) * 100).toFixed(2)}%` : undefined;
  const tooltipShift =
    activeFrac === null
      ? "-translate-x-1/2"
      : activeFrac < 0.12
        ? "translate-x-0"
        : activeFrac > 0.88
          ? "-translate-x-full"
          : "-translate-x-1/2";

  // Persistent close dot, drawn as an HTML element (not an SVG circle) so the
  // viewBox's non-uniform x/y scaling can't stretch it into an ellipse.
  const closeLeft = endPt !== undefined ? `${((endPt[0] / VB_W) * 100).toFixed(2)}%` : undefined;
  const closeTop = endPt !== undefined ? `${((endPt[1] / VB_H) * 100).toFixed(2)}%` : undefined;

  // Bottom time axis: first / middle / last close time of day.
  const midPoint = points[Math.floor((points.length - 1) / 2)];
  const axisFirst = formatTimeShort(firstPoint.closedAt);
  const axisMid = midPoint !== undefined ? formatTimeShort(midPoint.closedAt) : "";
  const axisLast = formatTimeShort(lastPoint.closedAt);

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex min-h-0 flex-1">
      <div
        ref={ref}
        className="relative min-w-0 flex-1"
        data-testid="intraday-pnl-chart"
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
      >
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          preserveAspectRatio="none"
          className="h-full w-full"
          aria-hidden="true"
        >
          <defs>
            <clipPath id={aboveClipId}>
              <rect x={0} y={0} width={VB_W} height={Math.max(zeroY, 0)} />
            </clipPath>
            <clipPath id={belowClipId}>
              <rect x={0} y={zeroY} width={VB_W} height={Math.max(VB_H - zeroY, 0)} />
            </clipPath>
            <linearGradient id={greenGradId} gradientUnits="userSpaceOnUse" x1={0} y1={PAD_Y} x2={0} y2={greenGradY2}>
              <stop offset="0" stopColor={SUCCESS} stopOpacity={0.24} />
              <stop offset="1" stopColor={SUCCESS} stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id={redGradId} gradientUnits="userSpaceOnUse" x1={0} y1={redGradY1} x2={0} y2={VB_H - PAD_Y}>
              <stop offset="0" stopColor={DANGER} stopOpacity={0.02} />
              <stop offset="1" stopColor={DANGER} stopOpacity={0.24} />
            </linearGradient>
          </defs>
          {/* Faint gridline at the session high. */}
          <line
            x1={0}
            y1={highY}
            x2={VB_W}
            y2={highY}
            stroke="var(--color-outline-variant)"
            strokeWidth={1}
            strokeOpacity={0.6}
            vectorEffect="non-scaling-stroke"
          />
          {/* Dashed $0 baseline. */}
          <line
            x1={0}
            y1={zeroY}
            x2={VB_W}
            y2={zeroY}
            stroke="var(--color-outline-variant)"
            strokeWidth={1}
            strokeDasharray="5 5"
            vectorEffect="non-scaling-stroke"
          />
          {points.length >= 2 ? (
            <>
              <g clipPath={`url(#${aboveClipId})`}>
                <path d={areaPath} fill={`url(#${greenGradId})`} stroke="none" />
                <path
                  d={linePath}
                  fill="none"
                  stroke={SUCCESS}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              </g>
              <g clipPath={`url(#${belowClipId})`}>
                <path d={areaPath} fill={`url(#${redGradId})`} stroke="none" />
                <path
                  d={linePath}
                  fill="none"
                  stroke={DANGER}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            </>
          ) : null}
        </svg>
        {/* Persistent close dot. */}
        {closeLeft !== undefined && closeTop !== undefined ? (
          <div
            aria-hidden="true"
            data-testid="intraday-pnl-chart-close-dot"
            className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-surface shadow"
            style={{ left: closeLeft, top: closeTop, backgroundColor: closeColor }}
          />
        ) : null}
        {/* Hover guide + marker + tooltip. */}
        {activeRow !== undefined && activeLeft !== undefined ? (
          <>
            <div
              className="pointer-events-none absolute inset-y-0 w-px bg-on-surface-muted/40 transition-[left] duration-150 ease-out motion-reduce:transition-none"
              style={{ left: activeLeft }}
            />
            {activeTop !== undefined ? (
              <div
                aria-hidden="true"
                data-testid="intraday-pnl-chart-marker"
                className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-surface bg-on-surface shadow transition-[left,top] duration-150 ease-out motion-reduce:transition-none"
                style={{ left: activeLeft, top: activeTop }}
              />
            ) : null}
            <div
              role="status"
              data-testid="intraday-pnl-chart-tooltip"
              className={`pointer-events-none absolute top-1 z-10 ${tooltipShift} whitespace-nowrap rounded border border-outline-variant bg-surface-container-high px-2 py-1 text-[10px] shadow-lg transition-[left] duration-150 ease-out motion-reduce:transition-none`}
              style={{ left: activeLeft }}
            >
              <div className="font-semibold tabular-nums text-on-surface">{activeRow.value}</div>
              <div className="text-on-surface-variant">{activeRow.label}</div>
            </div>
          </>
        ) : null}
      </div>
      {/* Right value axis: high / $0 / low at their true vertical positions. */}
      <div className="relative w-12 shrink-0 text-[10px] tabular-nums text-on-surface-muted">
        <span
          className="absolute right-0 -translate-y-1/2 whitespace-nowrap"
          style={{ top: `${((highY / VB_H) * 100).toFixed(2)}%` }}
        >
          {signedCompact(yMax, currency)}
        </span>
        <span
          className="absolute right-0 -translate-y-1/2 whitespace-nowrap"
          style={{ top: `${((zeroY / VB_H) * 100).toFixed(2)}%` }}
        >
          {signedCompact(0, currency)}
        </span>
        <span
          className="absolute right-0 -translate-y-1/2 whitespace-nowrap"
          style={{ top: `${((lowY / VB_H) * 100).toFixed(2)}%` }}
        >
          {signedCompact(yMin, currency)}
        </span>
      </div>
      </div>
      {/* Bottom time axis, aligned under the plot (pr-12 clears the value axis). */}
      <div className="flex justify-between pr-12 pt-1 text-[10px] tabular-nums text-on-surface-muted">
        <span>{axisFirst}</span>
        {points.length > 2 ? <span>{axisMid}</span> : null}
        <span>{axisLast}</span>
      </div>
    </div>
  );
}
